import { spineLoader, SpineView } from '@play-co/timestep-spine';
import { View } from '@play-co/timestep-core/ui';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';
import { clamp, Container } from '../../../odie';
import { PositionType } from '../../../lib/defs/types';
import { getMatch2AssetPath } from '../../util/assets';
import { blockPositionView } from '../../util/blockTools';
import { LaserBoxBlockProps } from './LaserBoxBlock';
import { CountBlockOptionDef } from '../../defs/block';
import { config } from '../../defs/config';
import sounds from 'src/lib/sounds';
import { waitForItPromise } from 'src/lib/utils';
import { getGameSceneMapRows } from '../../getGameScene';

// Pre-calculated horizontal grid dimension for clipping.
const GRID_WIDTH = config.map.columns * config.tile.size;
const HALF_TILE = config.tile.size / 2;

export const laserBoxAssets = {
  spineUnderlay: {
    loadMethod: spineLoader,
    url: getMatch2AssetPath(
      'blocks/laserBox/animation/spine.lasergun.underlay.obstacle',
    ),
  },
  spineOverlay: {
    loadMethod: spineLoader,
    url: getMatch2AssetPath(
      'blocks/laserBox/animation/spine.lasergun.overlay.obstacle',
    ),
  },
};

const CHARGE_SFX_DELAY = 0.14;
const SHOOT_SFX_DELAY = 1;
const DESTROY_SFX_DELAY = 0.35;

export class LaserBoxBlockView extends Container {
  public spineUnderlay?: SpineView;
  public spineOverlay?: SpineView;
  private _orientation: 'horizontal' | 'vertical';
  private _count = 0;
  private _spineClipContainer?: View;
  private _mapX = 0;
  private _mapY = 0;
  private _hasMapPosition = false;

  constructor(props: LaserBoxBlockProps, count: number) {
    super();
    blockPositionView(this, props);
    this._count = count;
    this._orientation = props.id === 'lbh' ? 'horizontal' : 'vertical';
    this.createSpines();
    this.updateState(true); // Initialize state animation
  }

  /**
   * Sets the map position and configures clipping bounds for the laser animation.
   * This ensures the laser beam doesn't overflow outside the puzzle grid area.
   */
  public setMapPosition(position: PositionType): void {
    this._mapX = position.x;
    this._mapY = position.y;
    this._hasMapPosition = true;
    this.updateClipBounds();
  }

  private updateClipBounds(): void {
    if (!this._spineClipContainer || !this._hasMapPosition) return;

    // Match the clip height to the active map's declared coordinate space.
    const rows = getGameSceneMapRows();
    this._spineClipContainer.style.height = rows * config.tile.size;

    // Calculate offset from spine position (0,0) to the grid's top-left corner
    const offsetX = -this._mapX * config.tile.size - HALF_TILE;
    const offsetY = -this._mapY * config.tile.size - HALF_TILE;

    // Position the clip container so its bounds align with the game grid
    this._spineClipContainer.style.x = offsetX;
    this._spineClipContainer.style.y = offsetY;

    // Update spine positions within the clip container to counter the offset
    if (this.spineUnderlay) {
      this.spineUnderlay.style.x = -offsetX;
      this.spineUnderlay.style.y = -offsetY;
    }
    if (this.spineOverlay) {
      this.spineOverlay.style.x = -offsetX;
      this.spineOverlay.style.y = -offsetY;
    }
  }

  private async createSpines() {
    // Do not create again if already created
    if (this.spineUnderlay || this.spineOverlay) return;

    // Ensure spine assets are loaded
    await Promise.all([
      spineLoader(laserBoxAssets.spineUnderlay.url),
      spineLoader(laserBoxAssets.spineOverlay.url),
    ]);

    // Match the initial clip height to the active map's declared coordinate space.
    const rows = getGameSceneMapRows();

    // Create a clip container to prevent the laser animation from overflowing the game grid
    this._spineClipContainer = new View({
      superview: this.view,
      clip: true,
      // Disable event handling so the clip container doesn't block puzzle interactions
      canHandleEvents: false,
      blockEvents: false,
      width: GRID_WIDTH,
      height: rows * config.tile.size,
    });

    const transform = {
      scale: 0.6,
      r: this._orientation === 'horizontal' ? Math.PI * 0.5 : 0,
    };

    this.spineUnderlay = new SpineView({
      superview: this._spineClipContainer,
      spinePath: laserBoxAssets.spineUnderlay.url,
      ...transform,
    });
    this.spineOverlay = new SpineView({
      superview: this._spineClipContainer,
      spinePath: laserBoxAssets.spineOverlay.url,
      ...transform,
    });

    // Update clip bounds if map position was already set
    this.updateClipBounds();

    // Update visual state after spines are created to ensure correct state when loading from save
    this.updateState(true);
  }

  public static assets() {
    return Object.values(laserBoxAssets);
  }

  public get count(): number {
    return this._count;
  }

  public set count(value: number) {
    this._count = clamp(value, 0, 4);
    this.updateState();
  }

  public get orientation() {
    return this._orientation;
  }

  public async updateState(instant?: boolean) {
    if (!this.spineUnderlay || !this.spineOverlay) return;

    // Determine animation stage 0-4, where 4 is the activation state.
    const stage = clamp(4 - this._count, 0, 4);

    // Skip stage 4 entirely as it will be handled via shoot effect
    if (stage === 4) return;

    const animations = [`stage${stage}`];

    // Transition is only applicable for stages 1 to 3
    if (!instant && stage > 0) {
      animations.unshift(`stage${stage - 1}_${stage}`);
    }

    // Only play sound effects during non-instant updates (not during save state loading)
    if (!instant) {
      void this.playChargeSFX();
    }

    // Play animation sequence
    this.playAnimation(animations, true);
  }

  public async playActivateAnimation() {
    void this.playChargeSFX();
    void this.playShootSFX();
    await this.playAnimation(['stage3_4', 'laser_fire', 'laser_loop'], true);
  }

  public async playDeactivateAnimation() {
    void this.playDestroyedSFX();
    await this.playAnimation(['laser_end']);
  }

  private async playAnimation(
    animation: string | string[],
    loopLast?: boolean,
  ) {
    if (!this.spineUnderlay || !this.spineOverlay) return;

    // Ensure animation is an array
    animation = Array.isArray(animation) ? animation : [animation];

    playSpineAnimationSequence(this.spineUnderlay, {
      sequence: animation,
      loopLast,
    });
    await playSpineAnimationSequence(this.spineOverlay, {
      sequence: animation,
      loopLast,
    });
  }

  private async playChargeSFX() {
    await waitForItPromise(CHARGE_SFX_DELAY * 1000);
    sounds.playSound('puzzle-laser-box-charge', 0.15);
  }

  private async playShootSFX() {
    await waitForItPromise(SHOOT_SFX_DELAY * 1000);
    sounds.playSound('puzzle-laser-box-shoot', 0.125);
  }

  private async playDestroyedSFX() {
    await waitForItPromise(DESTROY_SFX_DELAY * 1000);
    sounds.playSound('puzzle-hat-destroy', 0.35);
  }
}

export function _createLaserBoxBlockView(
  props: LaserBoxBlockProps,
  option: number,
): LaserBoxBlockView {
  const optionDef = props.options![option] as CountBlockOptionDef;
  return new LaserBoxBlockView(props, optionDef.count);
}
